import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import { MainLayout } from "../components/layout";
import leaveService, {
  type LeaveBalance,
  type LeaveType,
  type User,
} from "../services/leave.service";
import {
  getThaiFiscalYear,
  getThaiFiscalYearCE,
  getThaiFiscalYearDateRange,
} from "@leave-system/shared/utils";
import { useNavigate } from "../lib/router";

export const LeaveBalanceManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [editingBalance, setEditingBalance] = useState<{
    userId: string;
    leaveTypeId: string;
    totalDays: number;
    carriedOverDays: number;
    adjustmentDays: number;
  } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Thai Fiscal Year calculation
  const currentFiscalYearBE = getThaiFiscalYear(); // ปีงบประมาณ พ.ศ.
  const currentFiscalYearCE = getThaiFiscalYearCE(); // ปีงบประมาณ ค.ศ. (for API)
  const fiscalYearRange = getThaiFiscalYearDateRange(currentFiscalYearBE);

  // Check permission
  const canManage = currentUser?.role === "admin" || currentUser?.role === "hr";

  // Fetch users
  const { data: usersData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["users"],
    queryFn: () => leaveService.getUsers(),
    enabled: canManage,
  });

  // Fetch leave types
  const { data: leaveTypesData, isLoading: isLoadingLeaveTypes } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: () => leaveService.getLeaveTypes(),
    enabled: canManage,
  });

  // Fetch leave balances for selected user
  const { data: balancesData, isLoading: isLoadingBalances } = useQuery({
    queryKey: ["leaveBalances", selectedUserId],
    queryFn: async () => {
      console.log("🔍 Fetching balances for user:", selectedUserId);
      const data = await leaveService.getLeaveBalances(selectedUserId);
      console.log("📊 Balance data received:", data);
      return data;
    },
    enabled: !!selectedUserId && canManage,
  });

  // Update balance mutation
  const updateBalanceMutation = useMutation({
    mutationFn: async (data: {
      userId: string;
      leaveTypeId: string;
      totalDays: number;
      carriedOverDays: number;
      adjustmentDays: number;
    }) => {
      console.log("🔄 Updating leave balance:", data);
      return leaveService.updateLeaveBalance({
        userId: data.userId,
        leaveTypeId: data.leaveTypeId,
        totalDays: data.totalDays,
        carriedOverDays: data.carriedOverDays,
        adjustmentDays: data.adjustmentDays,
        year: currentFiscalYearCE,
      });
    },
    onSuccess: (response) => {
      console.log("✅ Leave balance updated:", response);
      queryClient.invalidateQueries({
        queryKey: ["leaveBalances", selectedUserId],
      });
      setShowEditModal(false);
      setEditingBalance(null);
      alert("✅ อัปเดตสิทธิ์การลาเรียบร้อยแล้ว!");
    },
    onError: (error: Error) => {
      console.error("❌ Failed to update leave balance:", error);
      alert(`❌ เกิดข้อผิดพลาด: ${error.message}`);
    },
  });

  // Initialize all balances mutation for a user
  const initializeBalancesMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!leaveTypesData?.leaveTypes) {
        throw new Error("ไม่พบข้อมูลประเภทการลา");
      }

      console.log("🔄 Initializing leave balances for user:", userId);

      // Create balances for all leave types with default values
      const results = await Promise.all(
        leaveTypesData.leaveTypes.map((leaveType: LeaveType) =>
          leaveService.updateLeaveBalance({
            userId: userId,
            leaveTypeId: leaveType.id,
            totalDays: leaveType.defaultDaysPerYear ?? 0,
            year: currentFiscalYearCE,
          })
        )
      );

      return results;
    },
    onSuccess: (response) => {
      console.log("✅ Leave balances initialized:", response);
      queryClient.invalidateQueries({
        queryKey: ["leaveBalances", selectedUserId],
      });
      alert("✅ สร้างสิทธิ์การลาเรียบร้อยแล้ว!");
    },
    onError: (error: Error) => {
      console.error("❌ Failed to initialize leave balances:", error);
      alert(`❌ เกิดข้อผิดพลาด: ${error.message}`);
    },
  });

  const handleInitializeBalances = () => {
    if (!selectedUserId) return;

    if (window.confirm("ต้องการสร้างสิทธิ์การลาสำหรับพนักงานคนนี้หรือไม่?")) {
      initializeBalancesMutation.mutate(selectedUserId);
    }
  };

  const handleEditBalance = (
    userId: string,
    leaveTypeId: string,
    currentTotal: number,
    currentCarriedOver: number,
    currentAdjustment: number
  ) => {
    setEditingBalance({
      userId,
      leaveTypeId,
      totalDays: currentTotal,
      carriedOverDays: currentCarriedOver,
      adjustmentDays: currentAdjustment,
    });
    setShowEditModal(true);
  };

  const handleSaveBalance = () => {
    if (editingBalance && editingBalance.totalDays >= 0) {
      updateBalanceMutation.mutate(editingBalance);
    }
  };

  if (!canManage) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 text-red-600 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-2">ไม่มีสิทธิ์เข้าถึง</h2>
            <p>
              คุณไม่มีสิทธิ์ในการจัดการสิทธิ์การลา เฉพาะ Admin และ HR เท่านั้น
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              กลับไปหน้าแรก
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MainLayout title="👥 จัดการสิทธิ์การลาพนักงาน">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                ตั้งค่าจำนวนวันลาสำหรับพนักงานแต่ละคนตามประเภทการลา
              </p>
              <p className="mt-1 text-sm text-blue-600 dark:text-blue-400 font-medium">
                📅 ปีงบประมาณ {currentFiscalYearBE} (
                {fiscalYearRange.startDateString} -{" "}
                {fiscalYearRange.endDateString})
              </p>
            </div>
          </div>
        </div>

        {/* User Selection */}
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            เลือกพนักงาน
          </label>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            disabled={isLoadingUsers}
            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">-- กรุณาเลือกพนักงาน --</option>
            {usersData?.users.map((user: User) => (
              <option key={user.id} value={user.id}>
                {user.name} ({user.employeeId}) - {user.role}
                {user.departmentId ? ` | Dept: ${user.departmentId}` : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Leave Balances Table */}
        {selectedUserId && (
          <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                สิทธิ์การลาปีงบประมาณ {currentFiscalYearBE}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {
                  usersData?.users.find((u: User) => u.id === selectedUserId)
                    ?.name
                }
              </p>
            </div>

            {isLoadingBalances ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">กำลังโหลดข้อมูล...</p>
              </div>
            ) : !balancesData || balancesData.length === 0 ? (
              <div className="p-12 text-center">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  ยังไม่มีข้อมูลสิทธิ์การลา
                </h3>
                <p className="text-gray-600 mb-4">
                  พนักงานยังไม่มีสิทธิ์การลาในปีงบประมาณนี้
                </p>
                <button
                  onClick={handleInitializeBalances}
                  disabled={
                    initializeBalancesMutation.isPending || isLoadingLeaveTypes
                  }
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {initializeBalancesMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังสร้างสิทธิ์...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                      สร้างสิทธิ์การลาสำหรับพนักงานนี้
                    </>
                  )}
                </button>
                <p className="text-sm text-gray-500 mt-3">
                  คลิกปุ่มด้านบนเพื่อสร้างสิทธิ์การลาตามค่าเริ่มต้นของแต่ละประเภท
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ประเภทการลา
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        สิทธิ์ทั้งหมด
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ใช้ไปแล้ว
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        คงเหลือ
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        การจัดการ
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoadingLeaveTypes ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-4 text-center">
                          กำลังโหลด...
                        </td>
                      </tr>
                    ) : (
                      leaveTypesData?.leaveTypes.map((leaveType: LeaveType) => {
                        const balance = balancesData?.find(
                          (b: LeaveBalance) => b.leaveTypeId === leaveType.id
                        );

                        // คำนวณค่าจาก API response
                        const totalEntitlement =
                          balance?.totalEntitlement ??
                          leaveType.defaultDaysPerYear ??
                          0;
                        const carriedOver = balance?.carriedOverDays ?? 0;
                        const adjustment = balance?.adjustmentDays ?? 0;
                        // แก้ไข: ใช้ absolute value เพราะ Backend อาจบันทึกเป็นค่าลบ
                        const usedDays = Math.abs(balance?.usedDays ?? 0);
                        const pendingDays = Math.abs(balance?.pendingDays ?? 0);

                        // สิทธิ์ทั้งหมด = entitlement + carried over + adjustment
                        const totalDays =
                          totalEntitlement + carriedOver + adjustment;

                        // คงเหลือ = สิทธิ์ทั้งหมด - ใช้ไป - รอพิจารณา
                        const availableDays =
                          totalDays - usedDays - pendingDays;

                        // Debug logging
                        console.log(
                          `📝 ${leaveType.name} (${leaveType.code}):`,
                          {
                            balance,
                            totalEntitlement,
                            carriedOver,
                            adjustment,
                            totalDays,
                            usedDays,
                            pendingDays,
                            availableDays,
                          }
                        );

                        return (
                          <tr key={leaveType.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <span className="text-2xl mr-2">
                                  {leaveType.icon || "📅"}
                                </span>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {leaveType.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {leaveType.code}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex flex-col items-center">
                                <span className="text-lg font-semibold text-blue-600">
                                  {totalDays} วัน
                                </span>
                                {(carriedOver > 0 || adjustment !== 0) && (
                                  <span className="text-xs text-gray-500 mt-1">
                                    ({totalEntitlement}
                                    {carriedOver > 0 && ` +${carriedOver} ย้าย`}
                                    {adjustment !== 0 &&
                                      ` ${adjustment > 0 ? "+" : ""}${adjustment} ปรับ`}
                                    )
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex flex-col items-center">
                                <span className="text-lg font-semibold text-orange-600">
                                  {usedDays} วัน
                                </span>
                                {pendingDays > 0 && (
                                  <span className="text-xs text-yellow-600 mt-1">
                                    (+{pendingDays} รอพิจารณา)
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span
                                className={`text-lg font-semibold ${
                                  availableDays > 0
                                    ? "text-green-600"
                                    : availableDays === 0
                                      ? "text-gray-600"
                                      : "text-red-600"
                                }`}
                              >
                                {availableDays} วัน
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <button
                                onClick={() =>
                                  handleEditBalance(
                                    selectedUserId,
                                    leaveType.id,
                                    totalEntitlement,
                                    carriedOver,
                                    adjustment
                                  )
                                }
                                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                              >
                                ✏️ แก้ไข
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg
              className="w-5 h-5 text-blue-600 mt-0.5 mr-2"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-900">
                คำแนะนำการใช้งาน
              </h4>
              <ul className="mt-2 text-sm text-blue-800 list-disc list-inside space-y-1">
                <li>เลือกพนักงานที่ต้องการจัดการสิทธิ์การลา</li>
                <li>คลิก "แก้ไข" เพื่อปรับจำนวนวันลาสำหรับแต่ละประเภท</li>
                <li>
                  จำนวนวันลาที่ตั้งค่าจะมีผลกับปีงบประมาณ {currentFiscalYearBE}{" "}
                  ({fiscalYearRange.startDateString} -{" "}
                  {fiscalYearRange.endDateString})
                </li>
                <li>
                  วันลาที่ใช้ไปแล้วจะไม่เปลี่ยนแปลง
                  มีเฉพาะสิทธิ์ทั้งหมดเท่านั้นที่เปลี่ยน
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && editingBalance && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">แก้ไขสิทธิ์การลา</h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                ประเภทการลา
              </label>
              <p className="text-lg text-gray-900 dark:text-gray-100">
                {leaveTypesData?.leaveTypes.find(
                  (lt: LeaveType) => lt.id === editingBalance.leaveTypeId
                )?.name || "N/A"}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                พนักงาน
              </label>
              <p className="text-lg text-gray-900 dark:text-gray-100">
                {usersData?.users.find(
                  (u: User) => u.id === editingBalance.userId
                )?.name || "N/A"}
              </p>
            </div>

            <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                ℹ️ ข้อมูลปัจจุบัน
              </h4>
              {(() => {
                const balance = balancesData?.find(
                  (b: LeaveBalance) =>
                    b.leaveTypeId === editingBalance.leaveTypeId
                );
                const leaveType = leaveTypesData?.leaveTypes.find(
                  (lt: LeaveType) => lt.id === editingBalance.leaveTypeId
                );
                const totalEntitlement =
                  balance?.totalEntitlement ??
                  leaveType?.defaultDaysPerYear ??
                  0;
                const carriedOver = balance?.carriedOverDays ?? 0;
                const adjustment = balance?.adjustmentDays ?? 0;
                // แก้ไข: ใช้ absolute value เพราะ Backend อาจบันทึกเป็นค่าลบ
                const usedDays = Math.abs(balance?.usedDays ?? 0);
                const pendingDays = Math.abs(balance?.pendingDays ?? 0);
                const totalDays = totalEntitlement + carriedOver + adjustment;
                const availableDays = totalDays - usedDays - pendingDays;

                return (
                  <div className="space-y-1 text-sm text-blue-800">
                    <div className="flex justify-between">
                      <span>สิทธิ์พื้นฐาน:</span>
                      <span className="font-semibold">
                        {totalEntitlement} วัน
                      </span>
                    </div>
                    {carriedOver > 0 && (
                      <div className="flex justify-between">
                        <span>ยกมาจากปีก่อน:</span>
                        <span className="font-semibold">
                          +{carriedOver} วัน
                        </span>
                      </div>
                    )}
                    {adjustment !== 0 && (
                      <div className="flex justify-between">
                        <span>ปรับปรุง:</span>
                        <span className="font-semibold">
                          {adjustment > 0 ? "+" : ""}
                          {adjustment} วัน
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                      <span className="font-medium">รวมสิทธิ์ทั้งหมด:</span>
                      <span className="font-bold text-blue-900">
                        {totalDays} วัน
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>ใช้ไปแล้ว:</span>
                      <span className="font-semibold text-orange-700">
                        -{usedDays} วัน
                      </span>
                    </div>
                    {pendingDays > 0 && (
                      <div className="flex justify-between">
                        <span>รอพิจารณา:</span>
                        <span className="font-semibold text-yellow-700">
                          -{pendingDays} วัน
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between border-t border-blue-300 pt-1 mt-1">
                      <span className="font-medium">คงเหลือ:</span>
                      <span
                        className={`font-bold ${availableDays > 0 ? "text-green-700" : "text-red-700"}`}
                      >
                        {availableDays} วัน
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                จำนวนวันลาพื้นฐาน (วัน) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                max="365"
                value={editingBalance.totalDays}
                onChange={(e) =>
                  setEditingBalance({
                    ...editingBalance,
                    totalDays: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="ระบุจำนวนวัน"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                สิทธิ์พื้นฐานตามระเบียบของบริษัทสำหรับปีงบประมาณ{" "}
                {currentFiscalYearBE}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                วันลายกมาจากปีก่อน (วัน)
              </label>
              <input
                type="number"
                min="0"
                max="365"
                value={editingBalance.carriedOverDays}
                onChange={(e) =>
                  setEditingBalance({
                    ...editingBalance,
                    carriedOverDays: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="ระบุจำนวนวัน"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                สิทธิ์การลาที่คงเหลือจากปีงบประมาณ {currentFiscalYearBE - 1}{" "}
                ที่ยกมา
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                ปรับปรุงพิเศษ (วัน)
              </label>
              <input
                type="number"
                min="-365"
                max="365"
                value={editingBalance.adjustmentDays}
                onChange={(e) =>
                  setEditingBalance({
                    ...editingBalance,
                    adjustmentDays: parseInt(e.target.value) || 0,
                  })
                }
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder="ระบุจำนวนวัน (+ หรือ -)"
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                ใช้สำหรับปรับปรุงพิเศษ เช่น เพิ่มวันลาตามอายุงาน
                หรือหักวันลาที่ใช้ผิดพลาด (ใส่ค่าลบได้)
              </p>
            </div>

            {/* Preview calculation */}
            <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-900 mb-2">
                📊 ผลรวมหลังแก้ไข
              </h4>
              {(() => {
                const balance = balancesData?.find(
                  (b: LeaveBalance) =>
                    b.leaveTypeId === editingBalance.leaveTypeId
                );
                const usedDays = Math.abs(balance?.usedDays ?? 0);
                const pendingDays = Math.abs(balance?.pendingDays ?? 0);
                const newTotalDays =
                  editingBalance.totalDays +
                  editingBalance.carriedOverDays +
                  editingBalance.adjustmentDays;
                const newAvailableDays = newTotalDays - usedDays - pendingDays;

                return (
                  <div className="space-y-1 text-sm text-green-800">
                    <div className="flex justify-between">
                      <span>สิทธิ์พื้นฐาน:</span>
                      <span className="font-semibold">
                        {editingBalance.totalDays} วัน
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>ยกมาจากปีก่อน:</span>
                      <span className="font-semibold">
                        +{editingBalance.carriedOverDays} วัน
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>ปรับปรุงพิเศษ:</span>
                      <span className="font-semibold">
                        {editingBalance.adjustmentDays >= 0 ? "+" : ""}
                        {editingBalance.adjustmentDays} วัน
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-green-300 pt-1 mt-1">
                      <span className="font-medium">รวมสิทธิ์ทั้งหมด:</span>
                      <span className="font-bold text-green-900">
                        {newTotalDays} วัน
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>ใช้ไปแล้ว + รอพิจารณา:</span>
                      <span className="font-semibold text-orange-700">
                        -{usedDays + pendingDays} วัน
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-green-300 pt-1 mt-1">
                      <span className="font-medium">คงเหลือหลังแก้ไข:</span>
                      <span
                        className={`font-bold ${newAvailableDays >= 0 ? "text-green-700" : "text-red-700"}`}
                      >
                        {newAvailableDays} วัน
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingBalance(null);
                }}
                disabled={updateBalanceMutation.isPending}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
              >
                ยกเลิก
              </button>
              <button
                onClick={handleSaveBalance}
                disabled={
                  updateBalanceMutation.isPending ||
                  editingBalance.totalDays < 0
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {updateBalanceMutation.isPending ? "กำลังบันทึก..." : "บันทึก"}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default LeaveBalanceManagement;
